import type { VaultState } from 'src/pages/admin/services/vaultService';
import { parseApiError } from 'src/pages/admin/utils/decimal-input';

import { VAULTS_NS as NS } from './constants';
import { OPERATIONS_ENGINE_ERROR_KEYS } from './helpers';

/**
 * XBIT-4401 (C-06 amendment "contact links"): operations bind or update a vault's X handle and group chat. The bounds
 * are the engine's and creation's (onboarding.CheckContact), counted as code points after trimming.
 */
export const X_HANDLE_MAX = 64;
export const GROUP_CHAT_ID_MAX = 128;

/** What the engine refuses as not one line: a control character (line breaks and tab included) or a bidi control. */
// eslint-disable-next-line no-control-regex
const NOT_ONE_LINE = /[\u0000-\u001f\u007f-\u009f‪-‮⁦-⁩]/;

/** A link as typed: blank is fine (it clears the link); otherwise at most `max` code points, one line. */
export const validateContactLink = (value: string, max: number): string | undefined => {
  const trimmed = value.trim();
  if (Array.from(trimmed).length > max) return `${NS}.contact.errors.tooLong`;
  if (NOT_ONE_LINE.test(trimmed)) return `${NS}.contact.errors.oneLine`;
  return undefined;
};

/** Every state but CLOSED takes the edit; a pending application's vault is DRAFT from CATALOGUED on. */
export const canEditContact = (state: VaultState | null | undefined): boolean =>
  Boolean(state) && state !== 'CLOSED';

/** A refused edit in the console's words; a refused link is named by its label. */
export const contactErrorMessages = (
  error: unknown,
  translate: (key: string, options?: object) => string
): string[] => {
  const info = parseApiError(error);
  const key = (k: string) => `${NS}.contact.errors.${k}`;
  if (info.code === 'VAULT_SETTINGS_INVALID') {
    const field = (error as { body?: { field?: unknown } } | null)?.body?.field;
    const label = field === 'groupChatId' ? 'groupChatId' : 'xHandle';
    return [translate(key('linkInvalid'), { field: translate(`${NS}.contact.${label}`) })];
  }
  if (info.code === 'VAULT_STATE_INVALID') return [translate(key('closed'))];
  if (info.code === 'VAULT_NOT_FOUND') return [translate(key('notFound'))];
  if (info.status === 403) return [translate(key('forbidden'))];
  const engine = info.code ? OPERATIONS_ENGINE_ERROR_KEYS[info.code] : undefined;
  if (engine) return [translate(key(engine))];
  return [translate(key(info.status === 504 ? 'timeout' : 'failed'))];
};
